import {csrfToken,loadIntake,type IntakeItem} from './api';

export type QuoteAuthority = {identityId:string;authorityOrganizationId:string};
export type ConfirmationRequest = QuoteAuthority & {
  customerOrganizationId:string;orderId:string;orderVersion:number;detailsVersion:number;amountMinor:number;key:string;
};
export type ConfirmedTestQuote = {quoteId:string;quoteVersion:1;orderId:string;orderVersion:number;amountMinor:number};
const fail=():never=>{throw new Error('The TEST quote request is unavailable.');};
const record=(value:unknown):value is Record<string,unknown>=>typeof value==='object'&&value!==null&&!Array.isArray(value);
const positive=(value:unknown):value is number=>Number.isSafeInteger(value)&&(value as number)>0;
function id(value:string):void {if(!/^[a-f0-9]{8}-[a-f0-9]{4}-[1-5][a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/.test(value))fail();}
function cents(value:unknown):value is number {return Number.isSafeInteger(value)&&(value as number)>=50&&(value as number)<=99_999_999;}

/** Parse a human-entered USD decimal exactly; no floating point rounding or default tariff. */
export function testQuoteAmount(input:string):number {
  if(!/^(?:0|[1-9][0-9]{0,5})(?:\.[0-9]{1,2})?$/.test(input))return fail();
  const [whole,fraction='']=input.split('.');
  const amount=Number(whole)*100+Number(fraction.padEnd(2,'0'));
  if(!cents(amount))return fail();return amount;
}

export async function loadQuoteOwnedIntake(authority:QuoteAuthority):Promise<IntakeItem[]> {
  id(authority.identityId);id(authority.authorityOrganizationId);
  const rows=await loadIntake(authority.authorityOrganizationId);
  if(rows.length>50)return fail();
  const keys=new Set<string>();
  for(const row of rows){
    if(!positive(row.order_version)||!positive(row.details_version))return fail();
    const key=`${row.organization_id}:${row.order_id}`;if(keys.has(key))return fail();keys.add(key);
  }
  return rows;
}

function journalKey(authority:QuoteAuthority,row:Pick<IntakeItem,'organization_id'|'order_id'>):string {
  [authority.identityId,authority.authorityOrganizationId,row.organization_id,row.order_id].forEach(id);
  return `profenz.stripe-test.confirm.v1:${authority.authorityOrganizationId}:${authority.identityId}:${row.organization_id}:${row.order_id}`;
}
const keys=['identityId','authorityOrganizationId','customerOrganizationId','orderId','orderVersion','detailsVersion','amountMinor','key'].sort().join(',');

export function retainedConfirmation(authority:QuoteAuthority,row:IntakeItem):ConfirmationRequest|null {
  try {
    const raw=sessionStorage.getItem(journalKey(authority,row));if(raw===null)return null;
    const value:unknown=JSON.parse(raw);
    if(!record(value)||Object.keys(value).sort().join(',')!==keys||value.identityId!==authority.identityId
      ||value.authorityOrganizationId!==authority.authorityOrganizationId||value.customerOrganizationId!==row.organization_id
      ||value.orderId!==row.order_id||!positive(value.orderVersion)||!positive(value.detailsVersion)||!cents(value.amountMinor)
      ||typeof value.key!=='string')return fail();
    id(value.key);return value as ConfirmationRequest;
  }catch{return fail();}
}

export function confirmationMatches(request:ConfirmationRequest,row:IntakeItem):boolean {
  return row.state==='ready_for_review'&&request.customerOrganizationId===row.organization_id&&request.orderId===row.order_id
    &&request.orderVersion===row.order_version&&request.detailsVersion===row.details_version;
}

export function prepareConfirmation(authority:QuoteAuthority,row:IntakeItem,amountMinor:number):ConfirmationRequest {
  if(row.state!=='ready_for_review'||!positive(row.order_version)||!positive(row.details_version)||!cents(amountMinor))return fail();
  const old=retainedConfirmation(authority,row);
  if(old){if(!confirmationMatches(old,row)||old.amountMinor!==amountMinor)return fail();return old;}
  const request:ConfirmationRequest={...authority,customerOrganizationId:row.organization_id,orderId:row.order_id,
    orderVersion:row.order_version,detailsVersion:row.details_version,amountMinor,key:crypto.randomUUID()};
  try {
    sessionStorage.setItem(journalKey(authority,row),JSON.stringify(request));
    const saved=retainedConfirmation(authority,row);
    if(!saved||JSON.stringify(saved)!==JSON.stringify(request))return fail();return saved;
  }catch{return fail();}
}

/** Exactly one existing confirmation POST; replay authority and custody remain server-owned. */
export async function confirmStripeTestQuote(request:ConfirmationRequest,row:IntakeItem,signal?:AbortSignal):Promise<ConfirmedTestQuote> {
  if(!confirmationMatches(request,row))return fail();
  // Re-read the exact journal at the last boundary before transport, never invent a replacement key.
  const saved=retainedConfirmation(request,row);
  if(!saved||JSON.stringify(saved)!==JSON.stringify(request))return fail();
  try {
    const csrf=csrfToken();if(!csrf)return fail();
    const response=await fetch('/api/v1/internal/stripe-test/quotes',{method:'POST',credentials:'same-origin',cache:'no-store',signal,
      headers:{Accept:'application/json','Content-Type':'application/json','X-ProFenz-CSRF':csrf},
      body:JSON.stringify({customer_organization_id:request.customerOrganizationId,order_id:request.orderId,
        expected_order_version:request.orderVersion,expected_details_version:request.detailsVersion,amount_minor:request.amountMinor,idempotency_key:request.key})});
    if(!response.ok)return fail();
    const body:unknown=await response.json();const quote=record(body)?body.quote:null;
    if(!record(quote)||typeof quote.quote_id!=='string'||quote.quote_version!==1||quote.order_id!==request.orderId
      ||quote.order_version!==request.orderVersion||quote.amount_minor!==request.amountMinor||quote.currency!=='USD'
      ||quote.mode!=='test'||quote.state!=='confirmed')return fail();
    id(quote.quote_id);
    return {quoteId:quote.quote_id,quoteVersion:1,orderId:request.orderId,orderVersion:request.orderVersion,amountMinor:request.amountMinor};
  }catch{return fail();}
}
